// DomCare.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
} from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import { enqueueSnackbar } from "notistack";
import {
  createDomCare,
  getDomCare,
  updateDomCare,
} from "../../../api/services/domCare";
import { fetchCandidateSuggestions } from "../../../api/services/calender";
import { QK } from "../../../base/qk";
import CustomInput from "../../CustomForm/CustomInput";
import CustomRichText from "../../CustomForm/CustomRichText";
import FileUpload from "../../CustomForm/FileUpload";

const DomCare = () => {
  const { id: clientId } = useParams();

  const [showPassword, setShowPassword] = useState(false);
  const [domCareId, setDomCareId] = useState(null);

  const [preferredCarerInput, setPreferredCarerInput] = useState("");
  const [selectedPreferredCarer, setSelectedPreferredCarer] = useState(null);
  const [blockedCarerInput, setBlockedCarerInput] = useState("");
  const [selectedBlockedCarer, setSelectedBlockedCarer] = useState(null);

  const [formValues, setFormValues] = useState({
    access_instruction: "",
    access_risks: "",
    care_instructions: "",
    door_code: "",
    weekly_contracted_hours: "",
    number_of_carers: 1,
  });

  const { data: domCares, isLoading, refetch } = useQuery(
    [QK.domCare, clientId],
    () => getDomCare(clientId),
    {
      enabled: !!clientId,
      onSuccess: (data) => {
        const record = Array.isArray(data) ? data[0] : data;
        if (record) {
          setDomCareId(record.id);
          setFormValues({
            access_instruction: record.access_instruction || "",
            access_risks: record.access_risks || "",
            care_instructions: record.care_instructions || "",
            door_code: record.door_code || "",
            weekly_contracted_hours: record.weekly_contracted_hours || "",
            number_of_carers: record.number_of_carers || 1,
          });

          // preferred carer
          const pref = record.preferred_carers?.[0]?.carer;
          if (pref) {
            setSelectedPreferredCarer(pref.id);
            setPreferredCarerInput(`${pref.first_name} ${pref.last_name}`);
          }

          // blocked carer
          const block = record.blocked_carers?.[0]?.carer;
          if (block) {
            setSelectedBlockedCarer(block.id);
            setBlockedCarerInput(`${block.first_name} ${block.last_name}`);
          }
        }
      },
    }
  );

  // ---- Carer suggestions ----
  const { data: preferredCarerSuggestions, isLoading: isPreferredCarerLoading } =
    useQuery(
      ["preferredCarerSuggestions", preferredCarerInput],
      () => fetchCandidateSuggestions(preferredCarerInput),
      { enabled: !!preferredCarerInput && preferredCarerInput.length > 1 }
    );

  const { data: blockedCarerSuggestions, isLoading: isBlockedCarerLoading } =
    useQuery(
      ["blockedCarerSuggestions", blockedCarerInput],
      () => fetchCandidateSuggestions(blockedCarerInput),
      { enabled: !!blockedCarerInput && blockedCarerInput.length > 1 }
    );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleRichTextChange = (field) => (value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handlePreferredCarerSelection = (carer) => {
    setPreferredCarerInput(`${carer.first_name} ${carer.last_name}`);
    setSelectedPreferredCarer(carer.id);
  };

  const handleBlockedCarerSelection = (carer) => {
    setBlockedCarerInput(`${carer.first_name} ${carer.last_name}`);
    setSelectedBlockedCarer(carer.id);
  };

  // ---- Create Mutation ----
  const createMutation = useMutation(createDomCare, {
    onSuccess: (data) => {
      enqueueSnackbar("DOM Care created successfully", { variant: "success" });
      if (data?.id) setDomCareId(data.id);
      refetch();
    },
    onError: () => {
      enqueueSnackbar("Failed to create DOM Care", { variant: "error" });
    },
  });

  // ---- Update Mutation ----
  const updateMutation = useMutation(
    ({ id, data }) => updateDomCare(id, data),
    {
      onSuccess: () => {
        enqueueSnackbar("DOM Care updated successfully", {
          variant: "success",
        });
        refetch();
      },
      onError: () => {
        enqueueSnackbar("Failed to update DOM Care", { variant: "error" });
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("client", clientId);
    formData.append("access_instruction", formValues.access_instruction);
    formData.append("access_risks", formValues.access_risks);
    formData.append("care_instructions", formValues.care_instructions);
    formData.append("door_code", formValues.door_code);
    formData.append(
      "weekly_contracted_hours",
      parseInt(formValues.weekly_contracted_hours, 10) || 0
    );
    formData.append(
      "number_of_carers",
      parseInt(formValues.number_of_carers, 10) || 1
    );

    if (selectedPreferredCarer) {
      formData.append("preferred_carers", selectedPreferredCarer);
    }
    if (selectedBlockedCarer) {
      formData.append("blocked_carers", selectedBlockedCarer);
    }

    const fileInput = document.querySelector(
      'input[name="contractDocument"]'
    );
    if (fileInput && fileInput.files && fileInput.files.length > 0) {
      formData.append("contract_document", fileInput.files[0]);
    }

    // ---- create or update ----
    if (domCareId) {
      updateMutation.mutate({ id: domCareId, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const isSaving = createMutation.isLoading || updateMutation.isLoading;

  return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 4, px: 2 }}>
      <Paper
        component="form"
        onSubmit={handleSubmit}
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: "1200px",
          p: 4,
          border: "1px solid #e2e8f0",
          borderRadius: "12px",
        }}
      >
        <Typography
          variant="h5"
          align="center"
          sx={{ color: "primary.main", fontWeight: 700, py: 2, mb: 3 }}
        >
          DOM Care
        </Typography>

        {/* Row 1 */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <CustomInput
              label="Door Code"
              name="door_code"
              value={formValues.door_code}
              onChange={handleInputChange}
              fullWidth
              type={showPassword ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? (
                        <VisibilityOffIcon fontSize="small" />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <CustomInput
              label="Weekly Contracted Hours"
              name="weekly_contracted_hours"
              value={formValues.weekly_contracted_hours}
              onChange={handleInputChange}
              fullWidth
              type="number"
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <CustomInput
              label="No. of Carers"
              name="number_of_carers"
              value={formValues.number_of_carers}
              onChange={handleInputChange}
              fullWidth
              type="number"
            />
          </Box>
        </Box>

        {/* Access Instruction */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mb: 0.5,
              fontSize: "0.75rem",
              fontWeight: 500,
              color: "#475467",
            }}
          >
            Access Instruction
          </Typography>
          <CustomRichText
            value={formValues.access_instruction}
            onChange={handleRichTextChange("access_instruction")}
            placeholder="Write access instructions..."
          />
        </Box>

        {/* Access Risks */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mb: 0.5,
              fontSize: "0.75rem",
              fontWeight: 500,
              color: "#475467",
            }}
          >
            Access Risks
          </Typography>
          <CustomRichText
            value={formValues.access_risks}
            onChange={handleRichTextChange("access_risks")}
            placeholder="Write access risks..."
          />
        </Box>

        {/* Care Instructions */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mb: 0.5,
              fontSize: "0.75rem",
              fontWeight: 500,
              color: "#475467",
            }}
          >
            Care Instructions
          </Typography>
          <CustomRichText
            value={formValues.care_instructions}
            onChange={handleRichTextChange("care_instructions")}
            placeholder="Write care instructions..."
          />
        </Box>

        {/* Carers */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1, position: "relative" }}>
            <CustomInput
              label="Preferred Carers"
              name="preferredCarer"
              value={preferredCarerInput}
              onChange={(e) => setPreferredCarerInput(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonAddIcon sx={{ fontSize: 18, color: "#10B981" }} />
                  </InputAdornment>
                ),
              }}
            />
            {isPreferredCarerLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 0.5 }}>
                <CircularProgress size={14} />
              </Box>
            )}
            {preferredCarerSuggestions?.length > 0 && (
              <Paper
                sx={{
                  mt: 0.5,
                  maxHeight: 100,
                  overflow: "auto",
                  borderRadius: "6px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <List dense disablePadding>
                  {preferredCarerSuggestions.map((carer) => (
                    <ListItem
                      key={carer.id}
                      onClick={() => handlePreferredCarerSelection(carer)}
                      sx={{ cursor: "pointer", py: 0.25 }}
                    >
                      <ListItemText
                        primary={`${carer.first_name} ${carer.last_name}`}
                        primaryTypographyProps={{ fontSize: "0.75rem" }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Box>

          <Box sx={{ flex: 1, position: "relative" }}>
            <CustomInput
              label="Blocked Carers"
              name="blockedCarer"
              value={blockedCarerInput}
              onChange={(e) => setBlockedCarerInput(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonRemoveIcon sx={{ fontSize: 18, color: "#EF4444" }} />
                  </InputAdornment>
                ),
              }}
            />
            {isBlockedCarerLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 0.5 }}>
                <CircularProgress size={14} />
              </Box>
            )}
            {blockedCarerSuggestions?.length > 0 && (
              <Paper
                sx={{
                  mt: 0.5,
                  maxHeight: 100,
                  overflow: "auto",
                  borderRadius: "6px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <List dense disablePadding>
                  {blockedCarerSuggestions.map((carer) => (
                    <ListItem
                      key={carer.id}
                      onClick={() => handleBlockedCarerSelection(carer)}
                      sx={{ cursor: "pointer", py: 0.25 }}
                    >
                      <ListItemText
                        primary={`${carer.first_name} ${carer.last_name}`}
                        primaryTypographyProps={{ fontSize: "0.75rem" }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Box>
        </Box>

        {/* File Upload */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <FileUpload
              label="Upload Contract Document"
              name="contractDocument"
              fullWidth
            />
          </Box>
        </Box>

        {/* Submit */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button
            variant="contained"
            type="submit"
            disabled={isSaving}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              py: 1.5,
              borderRadius: "8px",
            }}
          >
            {isSaving
              ? "Saving..."
              : domCareId
              ? "Update DOM Care"
              : "Save DOM Care"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default DomCare;